import { supabase } from "./supabase";

// Bucket name for storing blog post images
export const BLOG_IMAGES_BUCKET = "blog-images";

/**
 * Uploads a file to Supabase storage using signed URLs
 * @param file The file to upload
 * @param path The path within the bucket to store the file
 * @returns The public URL of the uploaded file or null if upload failed
 */
export async function uploadFile(
  file: File,
  path: string
): Promise<string | null> {
  try {
    // Check if user is authenticated
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      console.error("User must be authenticated to upload files");
      return null;
    }

    // Create a signed URL for the upload
    const { data: urlData, error: urlError } = await supabase.storage
      .from(BLOG_IMAGES_BUCKET)
      .createSignedUploadUrl(path);

    if (urlError) {
      console.error("Failed to create signed URL:", urlError);
      return null;
    }

    // Use the signed URL to upload the file directly
    const uploadResponse = await fetch(urlData.signedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
        Authorization: `Bearer ${session.access_token}`,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      console.error("Upload failed:", await uploadResponse.text());
      return null;
    }

    // Get the public URL
    const { data: publicUrlData } = supabase.storage
      .from(BLOG_IMAGES_BUCKET)
      .getPublicUrl(path);

    return publicUrlData.publicUrl;
  } catch (error) {
    console.error(`Error processing file upload:`, error);
    return null;
  }
}

/**
 * Deletes a file from Supabase storage
 * @param path The path of the file to delete
 * @returns True if deletion was successful, false otherwise
 */
export async function deleteFile(path: string): Promise<boolean> {
  try {
    // Check if user is authenticated
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      console.error("User must be authenticated to delete files");
      return false;
    }

    const { error } = await supabase.storage
      .from(BLOG_IMAGES_BUCKET)
      .remove([path]);

    if (error) {
      console.error(`Error deleting file:`, error);
      return false;
    }

    return true;
  } catch (error) {
    console.error(`Error processing file deletion:`, error);
    return false;
  }
}

/**
 * Gets the public URL for a file in Supabase storage
 * @param path The path of the file
 * @returns The public URL of the file
 */
export function getPublicUrl(path: string): string {
  const { data } = supabase.storage.from(BLOG_IMAGES_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}
